import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { minutesToHours } from '@/helpers';
import { cn } from '@/lib/utils';
import { formatHours } from '@/modules/time-tracking/helpers/format-time-track.helper';
import { parseTimeInputToMinutes } from '@/modules/time-tracking/helpers/time-input-parser.helper';

/** Imperative handle exposed for popover-style consumers (e.g. quick-add). */
export interface DurationInputHandle {
  /** Parse the current text and return the minutes (or null if invalid). */
  getMinutes: (options?: { allowZero?: boolean }) => number | null;
  /** Select all text (used to focus + select on open). */
  select: () => void;
  focus: () => void;
}

export interface DurationInputProps {
  /** Current value in minutes. Use `null` for empty. */
  value: number | null;
  /** Fired with parsed minutes (0 when the text is invalid/empty). */
  onChange: (minutes: number) => void;
  /** Inline error message shown for unparseable input (and in `'manual'` mode, the only error). */
  error?: string | null;
  /** Upper bound in minutes. Exceeding it shows `maxError` (in `'blur'` mode). */
  maxMinutes?: number;
  /** Error shown when the parsed value exceeds `maxMinutes` (in `'blur'` mode). */
  maxError?: string;
  /**
   * Message shown below the field while there is no error — typically the
   * accepted-format hint. Hidden when an error is shown.
   */
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /**
   * Validation strategy:
   *  - `'blur'` (default): error appears on blur, then tracks live on every
   *    keystroke once the field has been blurred once. Best for inline form
   *    fields where you don't want to nag mid-first-entry.
   *  - `'manual'`: never derives its own error; the parent owns validation and
   *    passes `error` in. Best for popover/submit flows (e.g. quick-add).
   */
  validateOn?: 'blur' | 'manual';
  /** Called with the parsed minutes (or null) on blur — useful for `validateOn='manual'`. */
  onBlurParse?: (minutes: number | null) => void;
}

/**
 * Free-form duration input accepting `2h`, `1.5`, `30m`, `2:30`, `1h 30m`, etc.
 * (parsed by {@link parseTimeInputToMinutes}). Owns the text↔minutes conversion,
 * the canonical `Hh Mm` re-format on blur, and — in `'blur'` mode — its own
 * inline validation. The single source of truth for "type a duration" across the
 * app (time-tracking quick-add, time-off hourly days, …).
 *
 * See docs/ai/details/components.md → form inputs for usage.
 */
export const DurationInput = forwardRef<DurationInputHandle, DurationInputProps>(
  (
    {
      value,
      onChange,
      error,
      maxMinutes,
      maxError,
      hint,
      disabled = false,
      placeholder,
      ariaLabel,
      className,
      validateOn = 'blur',
      onBlurParse,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState(() => formatMinutes(value));
    const [touched, setTouched] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      getMinutes: options => parseTimeInputToMinutes(text, options),
      select: () => inputRef.current?.select(),
      focus: () => inputRef.current?.focus(),
    }));

    // Resync the visible text when the stored minutes change externally (form
    // reset / rebuild) and the field isn't being actively edited.
    useEffect(() => {
      if (!touched) setText(formatMinutes(value));
    }, [value, touched]);

    const commit = (raw: string): number => {
      const parsed = parseTimeInputToMinutes(raw);
      const nextMinutes = parsed ?? 0;
      onChange(nextMinutes);
      if (validateOn === 'blur') {
        if (parsed === null && raw.trim().length > 0) {
          setInternalError(error ?? null);
        } else if (maxMinutes != null && nextMinutes > maxMinutes) {
          setInternalError(maxError ?? error ?? null);
        } else {
          setInternalError(null);
        }
      }
      return nextMinutes;
    };

    const handleChange = (raw: string) => {
      setText(raw);
      // Re-validate live only after the first blur, so correcting is responsive.
      if (validateOn === 'blur' && touched) commit(raw);
    };

    const handleBlur = () => {
      setTouched(true);
      const nextMinutes = commit(text);
      if (nextMinutes > 0) setText(formatMinutes(nextMinutes));
      onBlurParse?.(parseTimeInputToMinutes(text));
    };

    // In manual mode the parent owns the error; in blur mode we use our own.
    const shownError = validateOn === 'manual' ? error : internalError;

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <Input
          ref={inputRef}
          value={text}
          onChange={event => handleChange(event.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={!!shownError}
          autoComplete="off"
          className={cn(shownError && 'border-destructive focus-visible:ring-destructive')}
        />
        {shownError ? (
          <span className="text-xs text-destructive">{shownError}</span>
        ) : hint ? (
          <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </div>
    );
  }
);

DurationInput.displayName = 'DurationInput';

/** Stored minutes → display string (e.g. 90 → "1h 30m", 0/null → ""). */
export const formatMinutes = (minutes: number | null | undefined): string =>
  minutes && minutes > 0 ? formatHours(minutesToHours(minutes)) : '';
